<template>
  <BaseSidebar
    v-model="modelValue"
    title="Histórico de edição"
    description="Consulte todas as alterações feitas nos plantões, com a opção de filtrar por período."
    :iconRight="mdiCalendarBlankOutline"
    :icon="mdiHistory"
    :sizeIcon="true"
    @openFilter="handleOpenFilter"
    :isIconRightActive="isOpenFilterHistory"
  >
    <FilterHistory
      :is-open="isOpenFilterHistory"
      @close="handleCloseFilter"
      @apply-filters="handleApplyFilters"
    />

    <div v-if="formattedDateFilter" class="mb-6 text-center flex">
      <span
        class="bg-white inline-flex items-center rounded-full border border-primary30 px-5 py-3 text-sm text-primary30"
      >
        <span>{{ formattedDateFilter }}</span>
        <button
          @click="clearDateFilter"
          class="ml-2 text-gray-500 hover:text-gray-700 font-bold focus:outline-none"
          aria-label="Limpar filtro"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="12"
            viewBox="0 0 10 12"
            fill="none"
          >
            <path
              d="M9.33008 10.3335L0.666585 1.66683"
              stroke="#2F7F91"
              stroke-width="1.33333"
              stroke-linecap="round"
            />
            <path
              d="M0.669922 10.3335L9.33341 1.66683"
              stroke="#2F7F91"
              stroke-width="1.33333"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </span>
    </div>

    <div class="relative">
      <Spinner
        v-if="loading && historyItems.length === 0"
        color="primary"
        size="5rem"
        width="0.5rem"
        class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50 mt-10"
      />

      <div :class="{ 'opacity-50': loading && historyItems.length > 0 }">
        <HistoryCard
          v-for="item in historyItems"
          :key="item.id"
          :type="item.type"
          :user="item.user"
          :createdAt="item.createdAt"
          :changes="item.changes"
          :timezone="doctorStationTimezone"
          :is-auto-permanence="item.isAutoPermanence"
          :permanence-note="item.permanenceNote"
          class="mb-4"
        />
        <div v-if="historyItems.length === 0 && !loading" class="text-center text-gray-500 p-10">
          <p>Nenhum histórico de edição encontrado para o período selecionado.</p>
        </div>
      </div>
    </div>

    <template #footer>
      <FilledButton
        v-if="hasMorePages"
        text="Carregar Mais"
        width="100%"
        class="!bg-primary30 mt-6"
        :disabled="loading"
        :loading="loading"
        @click="handleRequestNew"
      />
      <p v-else-if="historyItems.length > 0" class="text-center text-sm text-gray-400 mt-6">
        Fim do histórico
      </p>
    </template>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiHistory, mdiCalendarBlankOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import FilterHistory from '@/components/Filters/FilterHistory.vue'
import HistoryCard from '@/components/designSystem/Cards/HistoryCard.vue'
import EditHistoryService from '@/Network/Services/EditHistoryService'
import { EditHistoryItem } from '@/Network/Types/Responses/EditHistoryResponses'
import Spinner from '@/components/others/Spinner.vue'

const toast = useToast()
const editHistoryService = new EditHistoryService()

const props = defineProps<{
  isOpen: boolean
  doctorStationId: number | null
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

const isOpenFilterHistory = ref(false)
const appliedDateFilter = ref<{
  startDate: string | Date | null
  endDate: string | Date | null
} | null>(null)

const historyItems = ref<EditHistoryItem[]>([])
const loading = ref(false)
const currentPage = ref(1)
const totalItems = ref(0)
const itemsPerPage = 10

const hasMorePages = computed(() => historyItems.value.length < totalItems.value)

const modelValue = computed({
  get: () => props.isOpen,
  set: (value) => {
    emit('update:isOpen', value)
  }
})

const formatDate = (dateValue: string | Date | null): string => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
  if (isNaN(date.getTime())) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

const formattedDateFilter = computed(() => {
  const start = appliedDateFilter.value?.startDate
    ? formatDate(appliedDateFilter.value.startDate)
    : null
  const end = appliedDateFilter.value?.endDate ? formatDate(appliedDateFilter.value.endDate) : null
  if (start && end) return `${start} - ${end}`
  if (start) return `A partir de ${start}`
  if (end) return `Até ${end}`
  return ''
})

function handleApplyFilters(filters: {
  startDate: string | Date | null
  endDate: string | Date | null
}) {
  appliedDateFilter.value = filters
  isOpenFilterHistory.value = false
  fetchHistory(true)
}

function clearDateFilter() {
  appliedDateFilter.value = null
  fetchHistory(true)
}

function handleOpenFilter() {
  isOpenFilterHistory.value = !isOpenFilterHistory.value
}

function handleCloseFilter() {
  isOpenFilterHistory.value = false
}

function handleRequestNew() {
  if (hasMorePages.value) {
    currentPage.value += 1
    fetchHistory()
  }
}

const doctorStationTimezone = ref<string | undefined>()

async function fetchHistory(reset = false) {
  if (!props.doctorStationId) {
    historyItems.value = []
    return
  }
  if (loading.value) return

  if (reset) {
    currentPage.value = 1
    historyItems.value = []
    totalItems.value = 0
  }

  loading.value = true
  try {
    const response = await editHistoryService.getHistory({
      doctorStationId: props.doctorStationId,
      startDate: appliedDateFilter.value?.startDate,
      endDate: appliedDateFilter.value?.endDate,
      page: currentPage.value,
      limit: itemsPerPage
    })
    const newItems = response.data.filter((item) => item?.changes && item?.changes?.length > 0)
    historyItems.value.push(...newItems)
    if (response.count != null) {
      totalItems.value = response.count
    }
    doctorStationTimezone.value = response.doctorStationTimezone
  } catch (err) {
    console.error('Erro ao buscar histórico', err)
    toast.error('Erro ao carregar o histórico de edições.')
  } finally {
    loading.value = false
  }
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      fetchHistory(true)
    }
  },
  { immediate: true }
)
</script>
